/**
 * NotificationBell.jsx
 * The bell in the top bar: every problem Kreeboom has noticed, newest first.
 *
 * Checks every minute and whenever the window regains focus, which is when
 * someone has just come back to it. Deliberately quiet on failure — a bell that
 * cannot load must never take the page down with it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api.js';
import { Bell, Warning, Check } from './Icons.jsx';
import { formatLondon } from '../utils/time.js';

const POLL_MS = 60_000;

/** The first real line of a message — the rest is for the full notification. */
const firstLine = (text) =>
  String(text ?? '')
    .split('\n')
    .find((line) => line.trim()) ?? '';

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const wrap = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const data = await api.notifications.list();
      setItems(data.notifications ?? []);
      setUnread(data.unread ?? 0);
    } catch {
      /* the bell is never worth an error on the page */
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    window.addEventListener('focus', load);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', load);
    };
  }, [load]);

  // Closed by a click elsewhere or by Escape, like every other popover.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  async function openItem(n) {
    setOpen(false);
    if (!n.read_at) {
      try {
        const res = await api.notifications.read(n.id);
        setUnread(res.unread ?? 0);
        setItems((list) =>
          list.map((x) => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)),
        );
      } catch {
        /* reading it is still possible even if marking it failed */
      }
    }
    if (n.link) navigate(n.link);
  }

  async function readAll() {
    try {
      await api.notifications.read();
      setUnread(0);
      setItems((list) => list.map((x) => ({ ...x, read_at: x.read_at ?? new Date().toISOString() })));
    } catch {
      /* left as it was */
    }
  }

  return (
    <div className="bell" ref={wrap}>
      <button
        className="icon-btn bell__btn"
        onClick={() => {
          if (!open) load();
          setOpen((o) => !o);
        }}
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        title="Notifications"
      >
        <Bell />
        {unread > 0 && <span className="bell__count">{unread > 9 ? '9+' : unread}</span>}
      </button>

      {open && (
        <div className="bell__panel" role="dialog" aria-label="Notifications">
          <div className="bell__head">
            <strong>Notifications</strong>
            {unread > 0 && (
              <button className="btn btn--sm" onClick={readAll}>
                Mark all read
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <p className="bell__empty">
              Nothing has gone wrong. If a post fails or an account needs reconnecting, it will
              appear here.
            </p>
          ) : (
            <ul className="bell__list">
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    className={`bell__item bell__item--${n.severity}${n.read_at ? '' : ' is-unread'}`}
                    onClick={() => openItem(n)}
                  >
                    <span className="bell__icon" aria-hidden="true">
                      {n.severity === 'info' ? <Check size={15} /> : <Warning size={15} />}
                    </span>
                    <span className="bell__text">
                      <span className="bell__title">{n.title}</span>
                      {n.body && <span className="bell__body">{firstLine(n.body)}</span>}
                      <span className="bell__when">{formatLondon(n.created_at)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="bell__foot">
            <button
              className="btn btn--sm"
              onClick={() => {
                setOpen(false);
                navigate('/settings#notifications');
              }}
            >
              Notification settings
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
